import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import DriverPayoutSetup from '@/components/DriverPayoutSetup';
import { CountrySelect } from '@/components/CountrySelect';
import { getCountryFlag, getCountryName } from '@/lib/i18n';
import Navbar from '@/components/layout/Navbar';
import CarIconSelector from '@/components/CarIconSelector';
import ThemeSelector from '@/components/ThemeSelector';
import AuthGateDialog from '@/components/AuthGateDialog';
import { SportMultiSelect } from '@/components/sports/SportMultiSelect';
import { SportTagBadge } from '@/components/sports/SportTagBadge';
import { EmailPrefsCard } from '@/components/EmailPrefsCard';
import { 
  ArrowLeft,
  Camera,
  Loader2,
  Shield,
  Phone,
  Car,
  User,
  CheckCircle,
  XCircle,
  Upload,
  Star,
  Clock,
  Plus,
  Trash2,
  ChevronDown,
  Instagram,
  Facebook,
  Link2,
  Pencil,
  Save,
  Music,
  Eye,
  AlertTriangle,
  CheckCircle2,
  Lock,
  Mic2,
  CalendarPlus,
  ToggleLeft,
  Send,
  Palette,
  Gift,
  Users,
  Check,
  Bell,
  BadgeCheck
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import usePushNotifications from '@/hooks/usePushNotifications';

// Inline referral/invite section component
const InviteSection = ({ API }) => {
  const [loading, setLoading] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchReferral = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [linkRes, statsRes] = await Promise.all([
          axios.get(`${API}/referral/my-link`, { headers }),
          axios.get(`${API}/referral/stats`, { headers }),
        ]);
        setReferralData(linkRes.data);
        setStatsData(statsRes.data);
      } catch (err) {
        console.error('Failed to load referral data', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReferral();
  }, [API]);

  const handleCopy = async () => {
    if (!referralData?.referral_link) return;
    try {
      await navigator.clipboard.writeText(referralData.referral_link);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleShare = async () => {
    if (!referralData?.referral_link) return;
    const shareData = {
      title: 'Join me on RollM8s!',
      text: `Hey! Join RollM8s — the UK Skate, Wheels & Boards community ride-share platform. Use my invite link:`,
      url: referralData.referral_link,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch {}
    } else {
      handleCopy();
    }
  };

  if (loading) {
    return (
      <Card className="bg-card border-border/50">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  const count = referralData?.referral_count || 0;

  return (
    <div className="space-y-4">
      {/* Hero card */}
      <Card className="bg-gradient-to-br from-fuchsia-500/10 via-pink-500/5 to-transparent border-fuchsia-500/20 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-32 h-32 bg-fuchsia-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <CardContent className="pt-6 pb-5 space-y-4 relative">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-fuchsia-500/20 flex items-center justify-center">
              <Gift className="w-5 h-5 text-fuchsia-400" />
            </div>
            <div>
              <h3 className="font-semibold text-base">Invite a Friend</h3>
              <p className="text-xs text-muted-foreground">Share your link & grow the community</p>
            </div>
          </div>

          {/* Stat strip */}
          <div className="flex gap-3">
            <div className="flex-1 rounded-xl bg-background/60 border border-border/30 p-3 text-center">
              <p className="text-2xl font-bold text-fuchsia-400" data-testid="referral-count">{count}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Friends joined</p>
            </div>
            <div className="flex-1 rounded-xl bg-background/60 border border-border/30 p-3 text-center">
              <p className="text-2xl font-bold text-pink-400">{referralData?.referral_code || '—'}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Your code</p>
            </div>
          </div>

          {/* Link box */}
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-lg bg-background/70 border border-border/40 px-3 py-2.5 text-xs font-mono truncate select-all text-muted-foreground" data-testid="referral-link">
              {referralData?.referral_link || '...'}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="rounded-lg shrink-0"
              onClick={handleCopy}
              data-testid="copy-referral-link-btn"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
            </Button>
          </div>

          {/* Share button */}
          <Button
            className="w-full rounded-xl bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-medium"
            onClick={handleShare}
            data-testid="share-referral-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Share Invite Link
          </Button>
        </CardContent>
      </Card>

      {/* Referred friends list */}
      {statsData?.referrals?.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="w-4 h-4 text-fuchsia-400" />
              Friends who joined
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {statsData.referrals.map((r, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-background/50 border border-border/20">
                <div className="w-8 h-8 rounded-full bg-fuchsia-500/15 flex items-center justify-center text-xs font-bold text-fuchsia-400">
                  {(r.name || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{r.name || 'Anonymous'}</p>
                  <p className="text-[11px] text-muted-foreground">
                    Joined {r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                  </p>
                </div>
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: pushSubscribe } = usePushNotifications();
  const [pushLoading, setPushLoading] = useState(false);
  const [igHandle, setIgHandle] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyStatus, setVerifyStatus] = useState(null); // null | 'pending' | 'verified'
  const [verifyLoading, setVerifyLoading] = useState(false);
  const payoutsRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [newVehicle, setNewVehicle] = useState({ make: '', model: '', color: '', registration: '', fuel_type: 'petrol', car_icon: 'sedan' });
  const [addingVehicle, setAddingVehicle] = useState(false);
  const [deletingVehicle, setDeletingVehicle] = useState(false);
  
  // DVLA Vehicle Check State
  const [vehicleCheckLoading, setVehicleCheckLoading] = useState(false);
  const [vehicleCheckResult, setVehicleCheckResult] = useState(null);
  
  const [editingIconVehicle, setEditingIconVehicle] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState('sedan');
  const [savingSocial, setSavingSocial] = useState(false);
  const [editingBasicInfo, setEditingBasicInfo] = useState(false);
  const [editingSocial, setEditingSocial] = useState(false);
  const [musicGenres, setMusicGenres] = useState([]);
  const [savingMusic, setSavingMusic] = useState(false);

  // Profile tabs — guests land on Theme since it's the only thing
  // they can actually use without an account.
  const [activeTab, setActiveTab] = useState(() => {
    const requested = searchParams.get('tab');
    if (requested) return requested;
    return user ? 'profile' : 'appearance';
  });
  const [authGateOpen, setAuthGateOpen] = useState(false);
  const [authGateFeature, setAuthGateFeature] = useState('this');
  const tabScrollRef = useRef(null);
  const tabElRefs = useRef({});

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // Phase 2 — communities (sports) the user belongs to. Editable via "My Communities" card on profile tab.
  const [editingCommunities, setEditingCommunities] = useState(false);
  const [savingCommunities, setSavingCommunities] = useState(false);
  const [selectedSports, setSelectedSports] = useState(user?.sports || ['roller_skate']);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    selfie_url: user?.selfie_url || user?.picture || '',
    instagram_handle: user?.instagram_handle || '',
    facebook_handle: user?.facebook_handle || '',
    show_socials_on_profile: user?.show_socials_on_profile !== false,
    country: user?.country || 'GB',
    vehicle: user?.vehicle_info || user?.default_vehicle || {
      make: '',
      model: '',
      color: '',
      registration: '',
      fuel_type: 'petrol'
    }
  });

  // Tab definitions
  // Guests can only access the appearance/music tabs — everything else
  // needs an account. We still render the tab bar so they see what
  // becomes available after signing up.
  const ALL_TABS = [
    { id: 'profile', label: 'Profile', icon: User, color: 'text-blue-400', bg: 'bg-blue-400/15' },
    { id: 'invite', label: 'Invite', icon: Gift, color: 'text-fuchsia-400', bg: 'bg-fuchsia-400/15' },
    { id: 'password', label: 'Password', icon: Lock, color: 'text-amber-400', bg: 'bg-amber-400/15' },
    { id: 'socials', label: 'Socials', icon: Instagram, color: 'text-pink-400', bg: 'bg-pink-400/15' },
    { id: 'notifications', label: 'Notifications', icon: Bell, color: 'text-fuchsia-400', bg: 'bg-fuchsia-400/15' },
    { id: 'roles', label: 'Roles', icon: ToggleLeft, color: 'text-green-400', bg: 'bg-green-400/15' },
    { id: 'appearance', label: 'Theme', icon: Palette, color: 'text-purple-400', bg: 'bg-purple-400/15' },
    ...(musicGenres.length > 0 ? [{ id: 'music', label: 'Music', icon: Music, color: 'text-rose-400', bg: 'bg-rose-400/15' }] : []),
    ...(user?.capabilities?.driver ? [
      { id: 'payouts', label: 'Payouts', icon: Shield, color: 'text-emerald-400', bg: 'bg-emerald-400/15' },
      { id: 'vehicle', label: 'Vehicles', icon: Car, color: 'text-sky-400', bg: 'bg-sky-400/15' },
    ] : []),
  ];
  const GUEST_OPEN_TABS = new Set(['appearance', 'music']);
  const PROFILE_TABS = ALL_TABS;

  const handleTabClick = (tabId) => {
    // Guests can only open Theme + Music. Any other tab → auth gate.
    if (!user && !GUEST_OPEN_TABS.has(tabId)) {
      const label = ALL_TABS.find(t => t.id === tabId)?.label || 'this';
      setAuthGateFeature(label);
      setAuthGateOpen(true);
      return;
    }
    setActiveTab(tabId);
    const el = tabElRefs.current[tabId];
    if (el && tabScrollRef.current) {
      const container = tabScrollRef.current;
      const scrollLeft = Math.max(0, el.offsetLeft - container.offsetLeft - 4);
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  };

  // Land guests on the Theme tab by default so they immediately see
  // content (not a locked Profile tab body), and scroll the tab bar
  // so the active Theme pill is actually in view.
  useEffect(() => {
    if (!user && !GUEST_OPEN_TABS.has(activeTab)) {
      setActiveTab('appearance');
    }
    if (!user) {
      // give the tab refs time to mount, then bring Theme into view
      const t = setTimeout(() => {
        const el = tabElRefs.current['appearance'];
        const container = tabScrollRef.current;
        if (el && container) {
          const scrollLeft = Math.max(0, el.offsetLeft - container.offsetLeft - 4);
          container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
        }
      }, 150);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Sync formData when user data changes (e.g., after refresh)
  useEffect(() => {
    if (user) {
      const selfieUrl = user.selfie_url || user.picture || '';
      // Add cache-busting if URL doesn't already have query params
      const cacheBustedUrl = selfieUrl && !selfieUrl.includes('?') 
        ? selfieUrl + '?t=' + Date.now() 
        : selfieUrl;
      
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        phone: user.phone || prev.phone,
        selfie_url: cacheBustedUrl || prev.selfie_url,
        instagram_handle: user.instagram_handle || '',
        facebook_handle: user.facebook_handle || '',
        country: user.country || prev.country || 'GB',
        vehicle: user.vehicle_info || user.default_vehicle || prev.vehicle
      }));
      // Sync community selection to whatever the server has on file.
      if (Array.isArray(user.sports) && user.sports.length > 0) {
        setSelectedSports(user.sports);
      }
    }
  }, [user]);

  // Fetch music genres
  useEffect(() => {
    const fetchMusicGenres = async () => {
      try {
        const response = await axios.get(`${API}/music-genres`);
        setMusicGenres(response.data);
      } catch (error) {
        console.log('Failed to fetch music genres');
      }
    };
    fetchMusicGenres();
  }, []);

  // Switch to payouts tab + scroll to it when navigated from trip creation.
  // We re-run when capabilities flip so that if the user record refreshes
  // *after* mount (e.g. backend just promoted them to driver), we still
  // land them on the right tab once it becomes available.
  useEffect(() => {
    if (location.state?.scrollTo === 'payouts' && user?.capabilities?.driver) {
      setActiveTab('payouts');
      setTimeout(() => {
        if (payoutsRef.current) {
          payoutsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 400);
    }
  }, [location.state, user?.capabilities?.driver]);


  // Refresh user data on page load to get latest license status
  useEffect(() => {
    if (user && refreshUser) {
      refreshUser();
    }
  }, [user, refreshUser]);

  // Handle Stripe return
  useEffect(() => {
    if (searchParams.get('stripe_return') === 'true') {
      toast.success('Payout account setup complete! Check your status below.');
    } else if (searchParams.get('stripe_refresh') === 'true') {
      toast.info('Please complete your payout account setup.');
    }
  }, [searchParams]);

  // Fetch user's vehicles
  useEffect(() => {
    if (user?.has_been_driver || user?.role === 'driver') {
      fetchVehicles();
    }
  }, [user?.has_been_driver, user?.role]);

  // Fetch community verification status
  useEffect(() => {
    const fetchVerifyStatus = async () => {
      try {
        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
        const res = await axios.get(`${API}/auth/verification-status`, { headers: { Authorization: `Bearer ${token}` } });
        setVerifyStatus(res.data?.verification_status || (res.data?.community_verified ? 'verified' : null));
        if (res.data?.instagram_handle) setIgHandle(res.data.instagram_handle);
      } catch {}
    };
    if (user) fetchVerifyStatus();
  }, [user]);

  const fetchVehicles = async () => {
    setVehiclesLoading(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const response = await axios.get(`${API}/users/vehicles`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      const vehicleList = response.data || [];
      setVehicles(vehicleList);
      
      // Select first vehicle if none selected
      if (vehicleList.length > 0 && !selectedVehicleId) {
        setSelectedVehicleId(vehicleList[0].vehicle_id);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setVehiclesLoading(false);
    }
  };

  // DVLA Vehicle Check Function
  const checkVehicle = useCallback(async (registration) => {
    if (!registration || registration.length < 5) {
      setVehicleCheckResult(null);
      return;
    }
    
    const cleanReg = registration.replace(/\s/g, '').toUpperCase();
    setVehicleCheckLoading(true);
    
    try {
      const response = await axios.post(`${API}/vehicle/check`, {
        registration: cleanReg
      });
      
      setVehicleCheckResult(response.data);
      
      // Auto-fill vehicle details if found
      if (response.data.success && response.data.make) {
        setNewVehicle(prev => ({
          ...prev,
          make: response.data.make || prev.make,
          color: response.data.colour || prev.color,
          fuel_type: response.data.fuel_type?.toLowerCase() || prev.fuel_type
        }));
      }
    } catch (error) {
      console.error('Vehicle check error:', error);
    } finally {
      setVehicleCheckLoading(false);
    }
  }, []);

  // Check vehicle when registration changes (with debounce)
  useEffect(() => {
    if (!showAddVehicle) {
      setVehicleCheckResult(null);
      return;
    }
    
    const timer = setTimeout(() => {
      if (newVehicle.registration) {
        checkVehicle(newVehicle.registration);
      }
    }, 800);
    
    return () => clearTimeout(timer);
  }, [newVehicle.registration, showAddVehicle, checkVehicle]);

  const handleAddVehicle = async () => {
    if (!newVehicle.make || !newVehicle.model || !newVehicle.color || !newVehicle.registration) {
      toast.error('Please fill in all vehicle fields');
      return;
    }
    
    setAddingVehicle(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      
      // Include DVLA verification data
      const vehicleData = {
        ...newVehicle,
        dvla_verified: vehicleCheckResult?.success && vehicleCheckResult?.is_tax_valid && vehicleCheckResult?.is_mot_valid,
        tax_status: vehicleCheckResult?.tax_status,
        tax_due_date: vehicleCheckResult?.tax_due_date,
        mot_status: vehicleCheckResult?.mot_status,
        mot_expiry_date: vehicleCheckResult?.mot_expiry_date,
        is_tax_valid: vehicleCheckResult?.is_tax_valid || false,
        is_mot_valid: vehicleCheckResult?.is_mot_valid || false,
        dvla_check_date: vehicleCheckResult?.success ? new Date().toISOString() : null
      };
      
      const response = await axios.post(`${API}/users/vehicles`, vehicleData, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      toast.success('Vehicle added successfully');
      setShowAddVehicle(false);
      setNewVehicle({ make: '', model: '', color: '', registration: '', fuel_type: 'petrol', car_icon: 'sedan' });
      
      // Refresh vehicles
      await fetchVehicles();
      
      // Refresh user data
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vehicle');
    } finally {
      setAddingVehicle(false);
    }
  };

  const handleUpdateCarIcon = async () => {
    if (!editingIconVehicle) return;
    
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.put(`${API}/users/vehicles/${editingIconVehicle.vehicle_id}/icon`, 
        { car_icon: selectedIcon },
        {
          headers: { Authorization: `Bearer ${token}` },
          
        }
      );
      
      // Update local state
      setVehicles(prev => prev.map(v => 
        v.vehicle_id === editingIconVehicle.vehicle_id 
          ? { ...v, car_icon: selectedIcon }
          : v
      ));
      
      toast.success('Car icon updated!');
      setEditingIconVehicle(null);
      
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to update car icon');
    }
  };

  const handleDeleteVehicle = async () => {
    if (!vehicleToDelete) return;
    
    setDeletingVehicle(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.delete(`${API}/users/vehicles/${vehicleToDelete.vehicle_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      toast.success('Vehicle removed');
      setShowDeleteConfirm(false);
      setVehicleToDelete(null);
      
      // If we deleted the selected vehicle, clear selection
      if (selectedVehicleId === vehicleToDelete.vehicle_id) {
        setSelectedVehicleId(null);
      }
      
      // Refresh vehicles
      await fetchVehicles();
      
      // Refresh user data
      if (refreshUser) refreshUser();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to remove vehicle';
      toast.error(msg);
    } finally {
      setDeletingVehicle(false);
    }
  };

  const getSelectedVehicle = () => {
    return vehicles.find(v => v.vehicle_id === selectedVehicleId);
  };

  const handleSelfieUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploading(true);
    try {
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      
      // Upload to Google Cloud Storage
      const response = await axios.post(`${API}/upload/gcs`, {
        file_data: base64,
        filename: file.name,
        upload_type: 'selfie'
      }, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Add cache-busting parameter to force browser to reload the image
        const newUrl = response.data.url + '?t=' + Date.now();
        setFormData(prev => ({ ...prev, selfie_url: newUrl }));
        toast.success('Profile photo uploaded!');
        
        // Refresh user data from server
        if (refreshUser) {
          await refreshUser();
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const licenseInputRef = useRef(null);
  const [licenseUploading, setLicenseUploading] = useState(false);

  const handleLicenseUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }
    
    setLicenseUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      
      const uploadResponse = await axios.post(`${API}/upload/gcs`, {
        file_data: base64,
        filename: file.name,
        upload_type: 'license'
      }, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (uploadResponse.data.success) {
        await axios.post(`${API}/users/submit-license`, {
          license_url: uploadResponse.data.url
        }, { 
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success('License uploaded! Pending admin approval.');
        if (refreshUser) refreshUser();
      }
    } catch (error) {
      console.error('License upload error:', error);
      toast.error('Failed to upload license');
    } finally {
      setLicenseUploading(false);
    }
  };

  const handleVerifyLicense = async () => {
    // Trigger file input
    licenseInputRef.current?.click();
  };

  const handleResetLicense = async () => {
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.post(`${API}/users/reset-license`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      toast.success('License status reset. You can now upload a new license.');
      if (refreshUser) refreshUser();
    } catch (error) {
      console.error('Reset license error:', error);
      toast.error('Failed to reset license status');
    }
  };

  const handleSaveProfile = async () => {
    const nameChanged = formData.name !== (user?.name || '');
    if (nameChanged) {
      if (!formData.name || formData.name.trim() === '') {
        toast.error('Name is required');
        return;
      }
      if (!/^[A-Za-zÀ-ÖØ-öø-ÿ\s'\-]+$/.test(formData.name.trim())) {
        toast.error('Name can only contain letters, spaces, hyphens and apostrophes');
        return;
      }
      if (formData.name.trim().length < 2) {
        toast.error('Name must be at least 2 characters');
        return;
      }
    }
    setLoading(true);
    try {
      const payload = {
        phone: formData.phone,
        vehicle_info: formData.vehicle,
        country: formData.country,
      };
      if (nameChanged) payload.name = formData.name;
      await axios.put(`${API}/users/profile`, payload, {  });
      
      toast.success('Profile updated!');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSocial = async () => {
    setSavingSocial(true);
    try {
      await axios.put(`${API}/users/profile`, {
        instagram_handle: formData.instagram_handle,
        facebook_handle: formData.facebook_handle
      }, {  });
      
      toast.success('Social accounts updated!');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to update social accounts');
    } finally {
      setSavingSocial(false);
    }
  };

  // Phase 2 — save user's selected communities to their profile.
  const handleSaveCommunities = async () => {
    if (!selectedSports || selectedSports.length === 0) {
      toast.error('Pick at least one community');
      return;
    }
    setSavingCommunities(true);
    try {
      await axios.put(`${API}/users/profile`, { sports: selectedSports });
      toast.success('Communities updated!');
      setEditingCommunities(false);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update communities');
    } finally {
      setSavingCommunities(false);
    }
  };

  const handleSelectMusicGenre = async (genreId) => {
    setSavingMusic(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.put(
        `${API}/users/me/music-preference?genre_id=${genreId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Music preference saved!');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to save music preference');
    } finally {
      setSavingMusic(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= (rating || 0)
                ? 'fill-yellow-400 text-yellow-400' 
                : 'text-muted-foreground/30'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen" data-testid="my-profile-page">
      <Navbar />
      
      {/* Header */}
      <div className="px-4 pb-20 pt-8">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <h1 className="font-heading text-2xl">{user ? 'Profile Settings' : 'Customise the app'}</h1>
          {user && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => navigate(`/profile/${user?.user_id}`)}
              data-testid="view-public-profile-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              What Users See
            </Button>
          )}
        </div>
      </div>

      {/* Profile Content */}
      <main className="px-4 -mt-16 pb-8">
        <div className="max-w-2xl mx-auto space-y-6">

          {/* Profile Header Card — guests see a sign-up CTA card,
              signed-in users see their avatar + selfie uploader. */}
          {user ? (
          <Card className="bg-card border-border/50">
            <CardContent className="pt-8 sm:pt-6">
              <div className="flex flex-col items-center mb-2">
                <div className="relative">
                  <Avatar className="w-28 h-28 border-4 border-background shadow-xl">
                    <AvatarImage src={formData.selfie_url} />
                    <AvatarFallback className="bg-primary text-white text-3xl">
                      {user?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center shadow-lg hover:bg-primary/90 transition-colors"
                    disabled={uploading}
                    data-testid="upload-selfie-btn"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                  </button>
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*"
                    onClick={e => { e.target.value = null; }}
                    onChange={handleSelfieUpload}
                  />
                </div>
                <h2 className="text-xl font-bold mt-4">{user?.name}</h2>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {user?.average_rating ? `${user.average_rating.toFixed(1)} rating` : 'No ratings yet'}
                </p>
                {user?.badges && user.badges.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3 justify-center">
                    {user.badges.map((badge, i) => (
                      <Badge key={i} variant="outline" className="text-xs border-primary/30 text-primary">
                        {badge === 'community' && '💬 Community'}
                        {badge === 'licensed' && '📋 Licensed'}
                        {badge === 'photo' && <><Camera className="w-3 h-3" /> Photo</>}
                        {!['community', 'licensed', 'photo'].includes(badge) && badge}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          ) : (
          <Card className="bg-gradient-to-br from-primary/15 to-secondary/10 border-primary/30" data-testid="guest-profile-banner">
            <CardContent className="pt-6 pb-6 text-center space-y-3">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 ring-2 ring-primary/40 flex items-center justify-center">
                <User className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Browsing as guest</h2>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                  Theme &amp; Music are open to everyone — tap any other tab to register and unlock the full app.
                </p>
              </div>
              <div className="flex gap-2 justify-center pt-1">
                <Button
                  onClick={() => navigate(`/register?redirect=${encodeURIComponent(location.pathname)}`)}
                  className="rounded-full"
                  data-testid="guest-banner-register-btn"
                >
                  Register for free
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`)}
                  className="rounded-full"
                  data-testid="guest-banner-login-btn"
                >
                  Log in
                </Button>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Scrollable Tab Bar — under profile header */}
          <div ref={tabScrollRef} className="flex gap-3 overflow-x-auto py-1 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }} data-testid="profile-tab-bar">
            {PROFILE_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const isLockedForGuest = !user && !GUEST_OPEN_TABS.has(tab.id);
              return (
                <button
                  key={tab.id}
                  ref={el => tabElRefs.current[tab.id] = el}
                  onClick={() => handleTabClick(tab.id)}
                  data-testid={`profile-tab-${tab.id}`}
                  className={`relative flex-shrink-0 flex flex-col items-center gap-1.5 p-3 rounded-xl w-20 border-2 transition-all duration-200 ${
                    isActive
                      ? `border-primary/60 ${tab.bg}`
                      : 'border-border bg-card/50 hover:border-primary/30'
                  } ${isLockedForGuest ? 'opacity-30 grayscale' : ''}`}
                >
                  {isLockedForGuest && (
                    <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-background/80 border border-border/60 flex items-center justify-center">
                      <Lock className="w-2.5 h-2.5 text-muted-foreground" />
                    </span>
                  )}
                  <Icon className={`w-5 h-5 ${tab.color}`} />
                  <span className={`text-[11px] font-semibold ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ── Tab Content ── */}

          {/* Profile Tab — Basic Info */}
          {activeTab === 'profile' && (
          <>
          {/* Push Notifications */}
          {pushSupported && (
            <Card className="bg-card border-border/50 mb-4">
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
                      <Bell className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Push Notifications</p>
                      <p className="text-xs text-muted-foreground">
                        {pushSubscribed ? "Enabled — you'll get alerts on your device" : 'Get ride & booking alerts on your device'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={pushSubscribed}
                    disabled={pushSubscribed || pushLoading}
                    onCheckedChange={async () => {
                      setPushLoading(true);
                      try {
                        await pushSubscribe();
                        toast.success('Push notifications enabled!');
                      } catch (e) { toast.error('Could not enable notifications'); }
                      finally { setPushLoading(false); }
                    }}
                    data-testid="push-notifications-toggle"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Community Verification */}
          <Card className="bg-card border-border/50 mb-4">
            <CardContent className="py-4 px-5">
              {verifyStatus === 'verified' || user?.community_verified ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/15 flex items-center justify-center">
                    <BadgeCheck className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-blue-500 flex items-center gap-1.5">Community Verified</p>
                    <p className="text-xs text-muted-foreground">@{igHandle || user?.instagram_handle} confirmed</p>
                  </div>
                </div>
              ) : verifyStatus === 'pending' ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-amber-500">Verification Pending</p>
                      <p className="text-xs text-muted-foreground">Keep an eye on your DMs from @rollm8s</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter code from DM"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.toUpperCase())}
                      className="flex-1 font-mono tracking-widest text-center uppercase"
                      maxLength={6}
                      data-testid="verify-code-input"
                    />
                    <Button
                      size="sm"
                      disabled={verifyCode.length < 6 || verifyLoading}
                      className="rounded-lg"
                      onClick={async () => {
                        setVerifyLoading(true);
                        try {
                          const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                          await axios.post(`${API}/auth/verify-community-code`, { code: verifyCode }, { headers: { Authorization: `Bearer ${token}` } });
                          setVerifyStatus('verified');
                          toast.success('You are now community verified!');
                          refreshUser();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Invalid code');
                        } finally { setVerifyLoading(false); }
                      }}
                      data-testid="submit-verify-code-btn"
                    >
                      {verifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <BadgeCheck className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Get Community Verified</p>
                      <p className="text-xs text-muted-foreground">Follow @rollm8s on Instagram and verify your account</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Your Instagram handle"
                      value={igHandle}
                      onChange={(e) => setIgHandle(e.target.value.replace(/^@/, ''))}
                      className="flex-1"
                      data-testid="ig-handle-input"
                    />
                    <Button
                      size="sm"
                      disabled={!igHandle.trim() || verifyLoading}
                      className="rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                      onClick={async () => {
                        setVerifyLoading(true);
                        try {
                          const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                          await axios.post(`${API}/auth/request-verification`, { instagram_handle: igHandle }, { headers: { Authorization: `Bearer ${token}` } });
                          setVerifyStatus('pending');
                          toast.success('Verification requested! Keep an eye on your DMs from @rollm8s');
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Something went wrong');
                        } finally { setVerifyLoading(false); }
                      }}
                      data-testid="request-verify-btn"
                    >
                      {verifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify Me'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Basic Information
                </CardTitle>
                {!editingBasicInfo ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingBasicInfo(true)}
                    className="rounded-full"
                    data-testid="edit-basic-info-btn"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => {
                      handleSaveProfile();
                      setEditingBasicInfo(false);
                    }}
                    disabled={loading}
                    className="rounded-full"
                    data-testid="save-basic-info-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                {editingBasicInfo ? (
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-12 rounded-xl"
                    data-testid="name-input"
                  />
                ) : (
                  <div className="h-12 rounded-xl bg-muted/30 border border-border/50 flex items-center px-4 text-foreground">
                    {formData.name || user?.name}
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <div className="flex gap-2">
                  {editingBasicInfo ? (
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+44 7xxx xxx xxx"
                      className="h-12 rounded-xl flex-1"
                      data-testid="phone-input"
                    />
                  ) : (
                    <div className="h-12 rounded-xl bg-muted/30 border border-border/50 flex items-center px-4 text-foreground flex-1">
                      {formData.phone || user?.phone || 'Not set'}
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Not public — used for ride coordination only</p>
              </div>

              <div className="space-y-2">
                <Label>Country</Label>
                {editingBasicInfo ? (
                  <CountrySelect
                    value={formData.country}
                    onChange={(code) => setFormData({ ...formData, country: code })}
                    testId="profile-country-select"
                  />
                ) : (
                  <div className="h-12 rounded-xl bg-muted/30 border border-border/50 flex items-center px-4 text-foreground" data-testid="profile-country-display">
                    {getCountryFlag(formData.country) || '🌍'} {getCountryName(formData.country) || 'Not set'}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">Used to show events, shop listings and ride-shares near you and to display prices in your local currency.</p>
              </div>
            </CardContent>
          </Card>

          {/* Phase 2 — My Communities */}
          <Card className="bg-card border-border/50" data-testid="my-communities-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  My Communities
                </CardTitle>
                {!editingCommunities ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingCommunities(true)}
                    className="rounded-full"
                    data-testid="edit-communities-btn"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingCommunities(false);
                        setSelectedSports(user?.sports || ['roller_skate']);
                      }}
                      className="rounded-full"
                      data-testid="cancel-communities-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveCommunities}
                      disabled={savingCommunities}
                      className="rounded-full"
                      data-testid="save-communities-btn"
                    >
                      {savingCommunities ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                      Save
                    </Button>
                  </div>
                )}
              </div>
              <CardDescription className="pt-1">
                Pick the tribes you ride with. We use this to filter events, gear, ride-shares and feeds to your scene.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {editingCommunities ? (
                <SportMultiSelect
                  value={selectedSports}
                  onChange={setSelectedSports}
                  minSelected={1}
                />
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="my-communities-display">
                  {(user?.sports || ['roller_skate']).map((s) => (
                    <SportTagBadge key={s} sport={s} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          </>
          )}

          {/* Invite Tab */}
          {activeTab === 'invite' && (
            <InviteSection API={API} />
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                Change Password
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Current Password</Label>
                <div className="relative">
                  <Input
                    type={showCurrentPw ? 'text' : 'password'}
                    placeholder="Enter current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="pr-10"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowCurrentPw(!showCurrentPw)}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">New Password</Label>
                <div className="relative">
                  <Input
                    type={showNewPw ? 'text' : 'password'}
                    placeholder="Enter new password (min 6 chars)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowNewPw(!showNewPw)}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Confirm New Password</Label>
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  data-testid="confirm-new-password-input"
                />
              </div>
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !newPassword || !confirmNewPassword}
                className="w-full sm:w-auto"
                data-testid="change-password-btn"
              >
                {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                Update Password
              </Button>
            </CardContent>
          </Card>

          )}
          {/* Socials Tab */}
          {activeTab === 'socials' && (
          <Card className="bg-card border-border/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-primary" />
                    Social Accounts
                  </CardTitle>
                  <CardDescription>
                    Add your socials for admin to verify you're part of the community
                  </CardDescription>
                </div>
                {!editingSocial && (user?.instagram_handle || user?.facebook_handle) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingSocial(true)}
                    className="rounded-full"
                    data-testid="edit-social-btn"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Community Verified Badge - Only show if admin has verified */}
              {user?.community_verified ? (
                <div className="p-3 rounded-xl bg-primary/10 border border-primary/30 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-primary">Community Verified</p>
                    <p className="text-xs text-muted-foreground">Admin has confirmed you're part of the Skate, Wheels & Boards community</p>
                  </div>
                </div>
              ) : (user?.instagram_handle || user?.facebook_handle) ? (
                <div className="p-3 rounded-xl bg-secondary/10 border border-secondary/30 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-secondary" />
                  </div>
                  <div>
                    <p className="font-medium text-secondary">Pending Verification</p>
                    <p className="text-xs text-muted-foreground">Admin will review your social accounts</p>
                  </div>
                </div>
              ) : null}
              
              {/* Show edit form or read-only display */}
              {editingSocial || (!user?.instagram_handle && !user?.facebook_handle) ? (
                <>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Instagram className="w-4 h-4" />
                      Instagram
                    </Label>
                    <div className="flex gap-2 items-center">
                      <span className="text-muted-foreground">@</span>
                      <Input
                        value={formData.instagram_handle}
                        onChange={(e) => setFormData({ ...formData, instagram_handle: e.target.value.replace('@', '') })}
                        placeholder="your_handle"
                        className="h-12 rounded-xl flex-1"
                        data-testid="instagram-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Facebook className="w-4 h-4" />
                      Facebook
                    </Label>
                    <div className="flex gap-2 items-center">
                      <span className="text-muted-foreground">@</span>
                      <Input
                        value={formData.facebook_handle}
                        onChange={(e) => setFormData({ ...formData, facebook_handle: e.target.value.replace('@', '') })}
                        placeholder="your.name"
                        className="h-12 rounded-xl flex-1"
                        data-testid="facebook-input"
                      />
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => {
                      handleSaveSocial();
                      setEditingSocial(false);
                    }}
                    disabled={savingSocial}
                    className="w-full rounded-xl h-12"
                    data-testid="save-social-btn"
                  >
                    {savingSocial ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                    ) : (
                      'Submit for Verification'
                    )}
                  </Button>
                </>
              ) : (
                <div className="space-y-3">
                  {user?.instagram_handle && (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/50">
                      <Instagram className="w-5 h-5 text-muted-foreground" />
                      <span className="text-foreground">@{user.instagram_handle}</span>
                    </div>
                  )}
                  {user?.facebook_handle && (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/50">
                      <Facebook className="w-5 h-5 text-muted-foreground" />
                      <span className="text-foreground">@{user.facebook_handle}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-border/50">
                    <Label className="text-sm text-muted-foreground">Show on public profile</Label>
                    <button
                      type="button"
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.show_socials_on_profile ? 'bg-primary' : 'bg-muted'}`}
                      onClick={async () => {
                        const newVal = !formData.show_socials_on_profile;
                        setFormData({ ...formData, show_socials_on_profile: newVal });
                        try {
                          await axios.put(`${API}/users/profile`, { show_socials_on_profile: newVal }, { headers: { Authorization: `Bearer ${localStorage.getItem('rollm8s_token') || localStorage.getItem('token')}` } });
                          toast.success(newVal ? 'Socials visible on profile' : 'Socials hidden from profile');
                        } catch (err) { toast.error('Failed to update'); }
                      }}
                      data-testid="toggle-socials-visibility"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.show_socials_on_profile ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          )}
          {/* Roles Tab */}
          {activeTab === 'roles' && (
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ToggleLeft className="w-5 h-5 text-primary" />
                Account Capabilities
              </CardTitle>
              <CardDescription className="text-xs">Toggle what you do on rollm8s. Your profile adapts to match.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-2">
              {/* Driver Toggle */}
              <div className={`p-3 rounded-xl border transition-colors ${user?.capabilities?.driver ? 'border-primary/40 bg-primary/5' : 'border-border/50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${user?.capabilities?.driver ? 'bg-primary/20' : 'bg-muted'}`}>
                      <Car className={`w-4 h-4 ${user?.capabilities?.driver ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Driver</p>
                      <p className="text-[11px] text-muted-foreground">Offer rides, receive contributions</p>
                    </div>
                  </div>
                  <Switch 
                    checked={user?.capabilities?.driver || false}
                    onCheckedChange={async (checked) => {
                      try {
                        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                        await axios.put(`${API}/users/capabilities`, { driver: checked }, { headers: { Authorization: `Bearer ${token}` } });
                        await refreshUser();
                        toast.success(checked ? 'Driver mode activated!' : 'Driver mode deactivated');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to update');
                      }
                    }}
                    data-testid="cap-driver-toggle"
                  />
                </div>
                {user?.capabilities?.driver && !user?.license_verified && !user?.admin_driver_override && (
                  <div className="mt-2 p-2 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-start gap-2">
                    <Shield className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-blue-600 dark:text-blue-400">ID verification is handled by Stripe during payout setup — nothing extra needed here.</p>
                  </div>
                )}
                {!user?.capabilities?.driver && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Switch this on to offer lifts. ID verification is handled by Stripe during payout setup.
                  </p>
                )}
              </div>

              {/* Organiser Toggle */}
              <div className={`p-3 rounded-xl border transition-colors ${user?.capabilities?.organiser ? 'border-secondary/40 bg-secondary/5' : 'border-border/50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${user?.capabilities?.organiser ? 'bg-secondary/20' : 'bg-muted'}`}>
                      <CalendarPlus className={`w-4 h-4 ${user?.capabilities?.organiser ? 'text-secondary' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Organiser</p>
                      <p className="text-[11px] text-muted-foreground">Create events, sell tickets, My Corner</p>
                    </div>
                  </div>
                  <Switch 
                    checked={user?.capabilities?.organiser || false}
                    onCheckedChange={async (checked) => {
                      try {
                        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                        await axios.put(`${API}/users/capabilities`, { organiser: checked }, { headers: { Authorization: `Bearer ${token}` } });
                        await refreshUser();
                        toast.success(checked ? 'Organiser mode activated!' : 'Organiser mode deactivated');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to update');
                      }
                    }}
                    data-testid="cap-organiser-toggle"
                  />
                </div>
              </div>

              {/* DJ Toggle */}
              <div className={`p-3 rounded-xl border transition-colors ${user?.capabilities?.dj ? 'border-accent/40 bg-accent/5' : 'border-border/50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${user?.capabilities?.dj ? 'bg-accent/20' : 'bg-muted'}`}>
                      <Mic2 className={`w-4 h-4 ${user?.capabilities?.dj ? 'text-accent' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">DJ</p>
                      <p className="text-[11px] text-muted-foreground">My Corner, showcase your sets</p>
                    </div>
                  </div>
                  <Switch 
                    checked={user?.capabilities?.dj || false}
                    onCheckedChange={async (checked) => {
                      try {
                        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                        await axios.put(`${API}/users/capabilities`, { dj: checked }, { headers: { Authorization: `Bearer ${token}` } });
                        await refreshUser();
                        toast.success(checked ? 'DJ mode activated!' : 'DJ mode deactivated');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to update');
                      }
                    }}
                    data-testid="cap-dj-toggle"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          )}
          {/* Payouts Tab */}
          {activeTab === 'payouts' && user?.capabilities?.driver && (
            <DriverPayoutSetup />
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <EmailPrefsCard />
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <ThemeSelector />
          )}

          {/* Music Tab */}
          {activeTab === 'music' && musicGenres.length > 0 && (
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Music className="w-5 h-5 text-primary" />
                  Music Preference
                </CardTitle>
                <CardDescription>
                  Choose your vibe - this plays in the music player throughout the site
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  {musicGenres.map((genre) => (
                    <button
                      key={genre.genre_id}
                      onClick={() => handleSelectMusicGenre(genre.genre_id)}
                      disabled={savingMusic}
                      className={`p-3 rounded-xl text-center transition-all ${
                        user?.preferred_music_genre === genre.genre_id
                          ? 'bg-primary/20 border-2 border-primary text-primary'
                          : 'bg-muted/30 border border-border/50 hover:bg-muted/50'
                      }`}
                      data-testid={`music-genre-${genre.genre_id}`}
                    >
                      <span className="text-2xl block mb-1">{genre.icon}</span>
                      <span className="text-xs font-medium">{genre.name}</span>
                      {user?.preferred_music_genre === genre.genre_id && (
                        <CheckCircle className="w-4 h-4 mx-auto mt-1 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
                {!user?.preferred_music_genre && (
                  <p className="text-xs text-muted-foreground text-center mt-3">
                    Tap a genre to set your music preference
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Vehicle Tab */}
          {activeTab === 'vehicle' && user?.capabilities?.driver && (
            <>
              {/* Vehicle Management Card */}
              <Card className="bg-card border-border/50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Car className="w-5 h-5 text-accent" />
                        My Vehicles
                      </CardTitle>
                      <CardDescription>
                        Manage your registered vehicles
                      </CardDescription>
                    </div>
                    <Button
                      onClick={() => setShowAddVehicle(true)}
                      size="sm"
                      className="rounded-full"
                      data-testid="add-vehicle-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Vehicle
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {vehiclesLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : vehicles.length === 0 ? (
                    <div className="text-center py-8">
                      <Car className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground mb-4">No vehicles registered yet</p>
                      <Button
                        onClick={() => setShowAddVehicle(true)}
                        variant="outline"
                        className="rounded-full"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Your First Vehicle
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {vehicles.map((vehicle) => (
                        <div 
                          key={vehicle.vehicle_id}
                          className={`p-4 rounded-xl border transition-colors ${
                            selectedVehicleId === vehicle.vehicle_id 
                              ? 'bg-primary/10 border-primary/50' 
                              : 'bg-background/50 border-border/30 hover:border-border'
                          }`}
                        >
                          {/* Vehicle Info */}
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <p className="font-medium">
                              {vehicle.make} {vehicle.model}
                            </p>
                            {selectedVehicleId === vehicle.vehicle_id && (
                              <Badge className="bg-primary/20 text-primary text-xs">Default</Badge>
                            )}
                            {/* DVLA Verification Badge */}
                            {vehicle.dvla_verified ? (
                              <Badge className="bg-success/20 text-success text-xs">Verified</Badge>
                            ) : (
                              <Badge variant="outline" className="border-warning/50 text-warning text-xs">Unverified</Badge>
                            )}
                          </div>
                          
                          {/* Vehicle Details */}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mb-2">
                            <span>{vehicle.color}</span>
                            <span className="font-mono">{vehicle.registration}</span>
                            <span className="capitalize">{vehicle.fuel_type || 'Petrol'}</span>
                          </div>
                          
                          {/* Tax & MOT Status */}
                          {vehicle.dvla_check_date && (
                            <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3 text-xs">
                              <span className={vehicle.is_tax_valid ? 'text-success' : 'text-warning'}>
                                Tax: {vehicle.is_tax_valid ? '✓ Valid' : '✗ Invalid'}
                                {vehicle.tax_due_date && ` (${vehicle.tax_due_date})`}
                              </span>
                              <span className={vehicle.is_mot_valid ? 'text-success' : 'text-warning'}>
                                MOT: {vehicle.is_mot_valid ? '✓ Valid' : '✗ Invalid'}
                                {vehicle.mot_expiry_date && ` (${vehicle.mot_expiry_date})`}
                              </span>
                            </div>
                          )}
                          
                          {/* Action Buttons - Stacked on mobile */}
                          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/30">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingIconVehicle(vehicle);
                                setSelectedIcon(vehicle.car_icon || 'sedan');
                              }}
                              className="text-xs text-primary h-8"
                            >
                              <Car className="w-3 h-3 mr-1" />
                              Icon
                            </Button>
                            {selectedVehicleId !== vehicle.vehicle_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                                    await axios.put(`${API}/users/vehicles/${vehicle.vehicle_id}/select`, {}, {
                                      headers: { Authorization: `Bearer ${token}` },
                                      
                                    });
                                    setSelectedVehicleId(vehicle.vehicle_id);
                                    toast.success('Default vehicle updated');
                                  } catch (error) {
                                    toast.error('Failed to update default vehicle');
                                  }
                                }}
                                className="text-xs h-8"
                              >
                                Set Default
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setVehicleToDelete(vehicle);
                                setShowDeleteConfirm(true);
                              }}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10 text-xs h-8 ml-auto"
                              data-testid={`delete-vehicle-${vehicle.vehicle_id}`}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Driver Payout Setup */}
              <div ref={payoutsRef}></div>
            </>
          )}

          {/* Save Button — always visible */}
          <Button
            onClick={handleSaveProfile}
            disabled={loading}
            className="w-full h-14 rounded-xl bg-primary hover:bg-primary/90 font-bold text-lg"
            data-testid="save-profile-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </main>

      {/* Add Vehicle Dialog */}
      <Dialog open={showAddVehicle} onOpenChange={setShowAddVehicle}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Vehicle</DialogTitle>
            <DialogDescription>
              Add a new vehicle to your profile for offering rides.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Make *</Label>
                <Input
                  placeholder="e.g., Ford"
                  value={newVehicle.make}
                  onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })}
                  data-testid="new-vehicle-make"
                />
              </div>
              <div className="space-y-1">
                <Label>Model *</Label>
                <Input
                  placeholder="e.g., Focus"
                  value={newVehicle.model}
                  onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
                  data-testid="new-vehicle-model"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Colour *</Label>
                <Input
                  placeholder="e.g., Blue"
                  value={newVehicle.color}
                  onChange={(e) => setNewVehicle({ ...newVehicle, color: e.target.value })}
                  data-testid="new-vehicle-color"
                />
              </div>
              <div className="space-y-1">
                <Label className="flex items-center gap-1">
                  Registration *
                  {vehicleCheckLoading && <Loader2 className="w-3 h-3 animate-spin text-primary" />}
                </Label>
                <div className="relative">
                  <Input
                    placeholder="e.g., AB12 CDE"
                    value={newVehicle.registration}
                    onChange={(e) => setNewVehicle({ ...newVehicle, registration: e.target.value.toUpperCase() })}
                    data-testid="new-vehicle-registration"
                    className={`${
                      vehicleCheckResult?.success && vehicleCheckResult?.is_tax_valid && vehicleCheckResult?.is_mot_valid 
                        ? 'border-green-500' 
                        : vehicleCheckResult?.warning_message 
                          ? 'border-yellow-500' 
                          : ''
                    }`}
                  />
                  {vehicleCheckResult?.success && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      {vehicleCheckResult.is_tax_valid && vehicleCheckResult.is_mot_valid ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* DVLA Check Results */}
            {vehicleCheckResult && (
              <div className={`p-3 rounded-lg border ${
                vehicleCheckResult.success && vehicleCheckResult.is_tax_valid && vehicleCheckResult.is_mot_valid
                  ? 'bg-green-500/10 border-green-500/30'
                  : vehicleCheckResult.warning_message
                    ? 'bg-yellow-500/10 border-yellow-500/30'
                    : 'bg-muted/50 border-border/50'
              }`}>
                <div className="flex items-start gap-2">
                  {vehicleCheckResult.success ? (
                    vehicleCheckResult.is_tax_valid && vehicleCheckResult.is_mot_valid ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    )
                  ) : (
                    <XCircle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    {vehicleCheckResult.success ? (
                      <>
                        <p className="text-sm font-medium">
                          {vehicleCheckResult.make} - {vehicleCheckResult.colour}
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs">
                          <span className={vehicleCheckResult.is_tax_valid ? 'text-green-500' : 'text-yellow-500'}>
                            Tax: {vehicleCheckResult.is_tax_valid ? '✓ Valid' : '✗ ' + (vehicleCheckResult.tax_status || 'Invalid')}
                            {vehicleCheckResult.tax_due_date && ` (${vehicleCheckResult.tax_due_date})`}
                          </span>
                          <span className={vehicleCheckResult.is_mot_valid ? 'text-green-500' : 'text-yellow-500'}>
                            MOT: {vehicleCheckResult.is_mot_valid ? '✓ Valid' : '✗ ' + (vehicleCheckResult.mot_status || 'Invalid')}
                            {vehicleCheckResult.mot_expiry_date && ` (${vehicleCheckResult.mot_expiry_date})`}
                          </span>
                        </div>
                        {vehicleCheckResult.warning_message && (
                          <p className="text-xs text-yellow-500 mt-2">
                            ⚠️ {vehicleCheckResult.warning_message}. Vehicle will show as unverified.
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {vehicleCheckResult.error || 'Unable to verify vehicle'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label>Fuel Type</Label>
              <Select value={newVehicle.fuel_type} onValueChange={(v) => setNewVehicle({ ...newVehicle, fuel_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="petrol">Petrol</SelectItem>
                  <SelectItem value="diesel">Diesel</SelectItem>
                  <SelectItem value="electric">Electric</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Car Icon Selector */}
            <CarIconSelector
              value={newVehicle.car_icon}
              onChange={(icon) => setNewVehicle({ ...newVehicle, car_icon: icon })}
              carColor={newVehicle.color || 'pink'}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddVehicle(false)}>Cancel</Button>
            <Button onClick={handleAddVehicle} disabled={addingVehicle} data-testid="confirm-add-vehicle">
              {addingVehicle ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Vehicle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Vehicle Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Vehicle</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this vehicle from your profile?
            </DialogDescription>
          </DialogHeader>
          {vehicleToDelete && (
            <div className="p-4 rounded-xl bg-muted/30 my-4">
              <p className="font-medium">{vehicleToDelete.make} {vehicleToDelete.model}</p>
              <p className="text-sm text-muted-foreground">
                {vehicleToDelete.color} • {vehicleToDelete.registration}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setVehicleToDelete(null); }}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteVehicle} 
              disabled={deletingVehicle}
              data-testid="confirm-delete-vehicle"
            >
              {deletingVehicle ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Remove Vehicle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Car Icon Dialog */}
      <Dialog open={!!editingIconVehicle} onOpenChange={(open) => !open && setEditingIconVehicle(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Car Icon</DialogTitle>
            <DialogDescription>
              Choose an icon that represents your {editingIconVehicle?.make} {editingIconVehicle?.model}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <CarIconSelector
              value={selectedIcon}
              onChange={setSelectedIcon}
              carColor={editingIconVehicle?.color || 'pink'}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingIconVehicle(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateCarIcon}>
              <Car className="w-4 h-4 mr-2" />
              Save Icon
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auth gate — fired when a guest taps a locked profile tab. */}
      <AuthGateDialog
        open={authGateOpen}
        onClose={() => setAuthGateOpen(false)}
        feature={authGateFeature}
      />
    </div>
  );
};

export default MyProfilePage;
